import { relations } from 'drizzle-orm';
import { sql } from 'drizzle-orm';
import {
  boolean,
  check,
  index,
  integer,
  pgEnum,
  pgTable,
  serial,
  text,
  timestamp,
  uniqueIndex,
  varchar,
} from 'drizzle-orm/pg-core';
import { typeIdDataType as typeId } from '../utils/typeid';
import { application } from './application';
import { dashboardSession } from './dashboard-session';
import { hostedAuthDomain } from './hosted-auth-domain';
import { trackingDomain } from './tracking-domain';
import { user } from './user';
import { userIdentity } from './user-identity';

const SUBSCRIPTION_STATUS = [
  'active',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'past_due',
  'trialing',
  'unpaid',
  'paused',
] as const;

export const FREE_TRIAL_ACCOUNT_LIMIT = 10;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[number];

export const subscriptionStatusEnum = pgEnum('subscription_status', SUBSCRIPTION_STATUS);

export const subscription = pgTable(
  'subscription',
  {
    id: serial().primaryKey(),
    stripe_id: varchar({ length: 255 }).notNull(),
    status: subscriptionStatusEnum(),
    current_period_start: timestamp({ withTimezone: true, mode: 'string' }),
    current_period_end: timestamp({ withTimezone: true, mode: 'string' }),
    usage_subscription_item_stripe_id: varchar({ length: 255 }),
    usage_next_snapshot_at: timestamp({ withTimezone: true, mode: 'string' }),
    organization_id: integer().references(() => organization.id, { onDelete: 'cascade' }),
    is_current: boolean().notNull().default(true),
    cancellation_reason: text().$type<'cancellation_requested' | 'payment_failed' | 'payment_disputed'>(),
    updated_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
  },
  (t) => [
    uniqueIndex('subscription_stripe_id_uq').on(t.stripe_id),
    index('subscription_organization_id_idx').on(t.organization_id),
    index('subscription_organization_is_current_idx').on(t.organization_id, t.is_current),
    index('subscription_usage_next_snapshot_idx').on(t.usage_next_snapshot_at),
  ],
);

export const organization = pgTable(
  'organization',
  {
    id: serial().primaryKey(),
    public_id: typeId('organization', 'public_id').notNull(),
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    name: text().notNull(),
    stripe_customer_id: text(),
    brevo_company_id: text(),
    free_trial_ends_at: timestamp({ withTimezone: true, mode: 'string' }),
    free_trial_starts_at: timestamp({ withTimezone: true, mode: 'string' }),
    api_lock: boolean().notNull().default(false),
    api_lock_at: timestamp({ withTimezone: true, mode: 'string' }),
    force_mfa: boolean().notNull().default(false),
    account_limit: integer(),
    usage_initialized_at: timestamp({ withTimezone: true, mode: 'string' }),
    proxy_rules: text().$type<Record<string, string>>(),
  },
  (t) => [
    uniqueIndex('organization_public_id_uq').on(t.public_id),
    uniqueIndex('organization_stripe_customer_id_uq').on(t.stripe_customer_id),
    index('organization_api_lock_api_lock_at_idx').on(t.api_lock, t.api_lock_at),
    index('organization_api_lock_free_trial_ends_at_idx').on(t.api_lock, t.free_trial_ends_at),
    check('organization_account_limit_positive_ck', sql`${t.account_limit} IS NULL OR ${t.account_limit} > 0`),
  ],
);

export const subscriptionRelations = relations(subscription, ({ one }) => ({
  organization: one(organization, { fields: [subscription.organization_id], references: [organization.id] }),
}));

export const organizationRelations = relations(organization, ({ many }) => ({
  subscriptions: many(subscription),
  applications: many(application),
  hostedAuthDomains: many(hostedAuthDomain),
  trackingDomains: many(trackingDomain),
  members: many(organizationMember),
}));

const ROLES = ['owner', 'admin', 'member'] as const;

export type MembershipRole = (typeof ROLES)[number];

export const roleEnum = pgEnum('role', ROLES);

export const organizationMember = pgTable(
  'organization_member',
  {
    id: serial().primaryKey(),
    organization_id: integer()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    user_id: integer().references(() => user.id, { onDelete: 'cascade' }),
    created_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    role: roleEnum().notNull().default('member'),
    email: varchar({ length: 50 }).notNull(),
    pending: boolean().notNull().default(false),
  },
  (t) => [
    index('organization_member_user_id_idx').on(t.user_id),
    index('organization_member_organization_email_idx').on(t.organization_id, t.email),
  ],
);

export const organizationMemberRelations = relations(organizationMember, ({ one }) => ({
  organization: one(organization, { fields: [organizationMember.organization_id], references: [organization.id] }),
  user: one(user, { fields: [organizationMember.user_id], references: [user.id] }),
}));

export const userRelations = relations(user, ({ many }) => ({
  memberships: many(organizationMember),
  identities: many(userIdentity),
  sessions: many(dashboardSession),
}));
